import React, { useState, useEffect, useCallback } from 'react';
import correlationData from '../data/correlationMatrix.json';
import type { AssetType, CorrelationMatrix } from '../types/correlationMatrix';
import type { Allocation } from '../types/simulation';
import PortfolioSimulationChart from './PortfolioSimulationChart';
import CapitalMarketAssumptions from './CapitalMarketAssumptions';
import RiskReturnHeatmap from './RiskReturnHeatmap';
import type { AssetClass } from '../types/assetClass';
import ScenarioReport from './ScenarioReport';

interface Props {
  assetClasses: AssetClass[];
  allocation?: Partial<Allocation>;
  onAllocationChange?: (allocation: Allocation) => void;
  initialInvestment?: number;
  years?: number;
  riskTolerance?: number;
  monthlyContribution?: number;
  targetAmount?: number;
  username?: string;
}

const StrategicAssetAllocation: React.FC<Props> = ({ 
  assetClasses: initialAssetClasses,
  allocation: externalAllocation,
  onAllocationChange,
  initialInvestment = 100000,
  years = 10,
  riskTolerance = 5,
  monthlyContribution = 0,
  targetAmount = 0,
  username
}) => {
  const [localAssetClasses, setLocalAssetClasses] = useState<AssetClass[]>(initialAssetClasses);
  const [allocation, setAllocation] = useState<Allocation>(() => {
    const defaultAllocation: Allocation = {
      cash: 0,
      'fixed income': 0,
      equities: 0,
      'real assets': 0,
      alternatives: 0,
      crypto: 0
    };
    return externalAllocation ? { ...defaultAllocation, ...externalAllocation } : defaultAllocation;
  });

  const [totalAllocation, setTotalAllocation] = useState<number>(0);
  const [expectedReturn, setExpectedReturn] = useState<number>(0);
  const [portfolioVolatility, setPortfolioVolatility] = useState<number>(0);
  const [sharpeRatio, setSharpeRatio] = useState<number>(0);
  const [selectedAsset, setSelectedAsset] = useState<string | null>(null);
  const [selectedReport, setSelectedReport] = useState<number | null>(null);
  const [savedScenarios, setSavedScenarios] = useState<{
    name: string;
    allocation: Allocation;
    expectedReturn: number;
    volatility: number;
    sharpeRatio: number;
  }[]>([]);
  const riskFreeRate = 2.0; // Assuming 2% risk-free rate

  const handleSaveScenario = useCallback(() => {
    if (totalAllocation !== 100) {
      alert('Please ensure total allocation equals 100% before saving');
      return;
    }
    const scenarioName = `${riskTolerance <= 3 ? 'Conservative' : 
                          riskTolerance <= 6 ? 'Moderate' : 
                          'Aggressive'} (${years}y)`;
    setSavedScenarios(prev => [...prev, {
      name: scenarioName,
      allocation: { ...allocation },
      expectedReturn,
      volatility: portfolioVolatility,
      sharpeRatio
    }]);
  }, [totalAllocation, riskTolerance, years, allocation, expectedReturn, portfolioVolatility, sharpeRatio]);

  const handleRestoreScenario = useCallback((index: number) => {
    const scenario = savedScenarios[index];
    setAllocation(scenario.allocation);
    onAllocationChange?.(scenario.allocation);
  }, [savedScenarios, onAllocationChange]);

  const calculateAllocation = useCallback(() => {
    // Calculate base allocations based on risk tolerance and investment horizon
    const riskScore = (riskTolerance / 10); // 0.1 to 1.0
    const timeScore = (years / 30); // 0.033 to 1.0
    
    // Combined score affects the split between defensive and growth assets
    const combinedScore = (riskScore * 0.7 + timeScore * 0.3); // Weighted average
    
    // Initialize allocation object
    const newAllocation: Allocation = {
      cash: 0,
      'fixed income': 0,
      equities: 0,
      'real assets': 0,
      alternatives: 0,
      crypto: 0
    };

    // Defensive assets (decrease as risk/time increases)
    const defensiveTotal = 60 * (1 - combinedScore);
    newAllocation.cash = Math.round(defensiveTotal * 0.33);
    newAllocation['fixed income'] = Math.round(defensiveTotal * 0.67);

    // Growth assets (increase as risk/time increases)
    const growthTotal = 100 - defensiveTotal;
    const growthWeights = {
      'equities': 0.55,
      'real assets': 0.25,
      'alternatives': 0.15,
      'crypto': 0.05
    };
    
    Object.entries(growthWeights).forEach(([asset, weight]) => {
      newAllocation[asset as keyof Allocation] = Math.round(growthTotal * weight);
    });

    // Ensure minimum allocations
    newAllocation.cash = Math.max(newAllocation.cash, 5);
    newAllocation['fixed income'] = Math.max(newAllocation['fixed income'], 10);
    
    // Normalize to 100%
    const total = Object.values(newAllocation).reduce((sum, value) => sum + value, 0);
    if (total !== 100) {
      const scale = 100 / total;
      Object.keys(newAllocation).forEach(key => {
        newAllocation[key as keyof Allocation] = Math.round(newAllocation[key as keyof Allocation] * scale);
      });
    }

    setAllocation(newAllocation);
    onAllocationChange?.(newAllocation);
  }, [riskTolerance, years, onAllocationChange]);

  const handleAllocationChange = useCallback((type: keyof Allocation, value: number) => {
    const newAllocation: Allocation = { ...allocation, [type]: value };
    setAllocation(newAllocation);
    onAllocationChange?.(newAllocation);
  }, [allocation, onAllocationChange]);

  useEffect(() => {
    if (!externalAllocation) {
      calculateAllocation();
    }
  }, [riskTolerance, years, externalAllocation, calculateAllocation]);

  useEffect(() => {
    // Calculate total allocation
    const total = Object.values(allocation).reduce((sum, value) => sum + value, 0);
    setTotalAllocation(total);

    // Calculate expected return and volatility
    const weightedReturn = localAssetClasses.reduce((sum: number, assetClass: AssetClass) => {
      const assetKey = assetClass.name.toLowerCase() as keyof Allocation;
      return sum + (allocation[assetKey] * assetClass.longTermReturn) / 100;
    }, 0);

    // Calculate portfolio volatility with correlations
    let portfolioVariance = 0;
    localAssetClasses.forEach((asset1: AssetClass) => {
      const weight1 = allocation[asset1.name.toLowerCase() as keyof Allocation] / 100;
      localAssetClasses.forEach((asset2: AssetClass) => {
        const weight2 = allocation[asset2.name.toLowerCase() as keyof Allocation] / 100;
        const assetKey1 = asset1.name.toLowerCase() as AssetType;
        const assetKey2 = asset2.name.toLowerCase() as AssetType;
        const correlation = (correlationData as CorrelationMatrix).correlations[assetKey1]?.[assetKey2] ?? 0;
        const contribution = weight1 * weight2 * asset1.volatility * asset2.volatility * correlation;
        portfolioVariance += contribution;
      });
    });
    const weightedVolatility = Math.sqrt(portfolioVariance);

    // Calculate Sharpe Ratio
    const excessReturn = weightedReturn - riskFreeRate;
    const sharpeRatio = weightedVolatility > 0 ? excessReturn / weightedVolatility : 0;

    setExpectedReturn(weightedReturn);
    setPortfolioVolatility(weightedVolatility);
    setSharpeRatio(sharpeRatio);
  }, [allocation, localAssetClasses, riskFreeRate]);

  const Tooltip = ({ text }: { text: string }) => {
    const [isVisible, setIsVisible] = useState(false);
    
    return (
      <div 
        style={{ 
          position: 'relative', 
          display: 'inline-block',
          marginLeft: '4px',
          cursor: 'help'
        }}
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
      >
        <span style={{ 
          color: '#666',
          border: '1px solid #ccc',
          borderRadius: '50%',
          width: '16px',
          height: '16px',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '12px'
        }}>?</span>
        <div style={{ 
          position: 'absolute',
          bottom: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: 'rgba(0,0,0,0.8)',
          color: 'white',
          padding: '8px 12px',
          borderRadius: '4px',
          fontSize: '12px',
          whiteSpace: 'nowrap',
          visibility: isVisible ? 'visible' : 'hidden',
          opacity: isVisible ? 1 : 0,
          transition: 'opacity 0.2s',
          zIndex: 1,
          marginBottom: '8px'
        }}>
          {text}
        </div>
      </div>
    );
  };

  return (
    <div className="strategic-asset-allocation" style={{ marginBottom: '2rem' }}>
      {/* Goal Parameters Section */}
      <div style={{ 
        backgroundColor: 'white', 
        padding: '20px', 
        borderRadius: '8px',
        marginBottom: '20px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <h3 style={{ marginTop: 0, marginBottom: '10px' }}>Goal Parameters</h3>
        <p style={{ color: '#666', marginBottom: '15px' }}>
          Your investment strategy is tailored based on these parameters. A longer time horizon and higher risk tolerance 
          typically allow for more growth-oriented assets, while shorter horizons or lower risk tolerance favor more 
          conservative allocations.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
          <div>
            <div style={{ color: '#666', marginBottom: '5px' }}>Initial Amount</div>
            <div style={{ fontSize: '1.2em', fontWeight: 'bold' }}>
              {new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              }).format(initialInvestment)}
            </div>
          </div>
          <div>
            <div style={{ color: '#666', marginBottom: '5px' }}>Monthly Contribution</div>
            <div style={{ fontSize: '1.2em', fontWeight: 'bold' }}>
              {new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              }).format(monthlyContribution)}
            </div>
          </div>
          <div>
            <div style={{ color: '#666', marginBottom: '5px' }}>Time Horizon</div>
            <div style={{ fontSize: '1.2em', fontWeight: 'bold' }}>{years} years</div>
          </div>
          <div>
            <div style={{ color: '#666', marginBottom: '5px' }}>Risk Tolerance</div>
            <div style={{ fontSize: '1.2em', fontWeight: 'bold' }}>{riskTolerance}/10</div>
          </div>
          <div>
            <div style={{ color: '#666', marginBottom: '5px' }}>Strategy</div>
            <div style={{ fontSize: '1.2em', fontWeight: 'bold' }}>
              <span style={{
                color: riskTolerance <= 3 ? '#4caf50' : 
                       riskTolerance <= 6 ? '#ff9800' : 
                       '#f44336'
              }}>
                {riskTolerance <= 3 ? 'Conservative' : 
                 riskTolerance <= 6 ? 'Moderate' : 
                 'Aggressive'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Capital Market Assumptions */}
      <div style={{ 
        backgroundColor: 'white', 
        padding: '20px', 
        borderRadius: '8px',
        marginBottom: '20px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <CapitalMarketAssumptions 
          assetClasses={localAssetClasses}
          onUpdate={(updatedAssetClasses: AssetClass[]) => {
            setLocalAssetClasses(updatedAssetClasses);
          }}
        />
      </div>

      {/* Asset Allocation Section */}
      <div style={{ 
        backgroundColor: '#f8f9fa', 
        padding: '20px', 
        borderRadius: '8px'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '20px'
        }}>
          <h3 style={{ margin: 0 }}>Asset Allocation:</h3>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={handleSaveScenario}
              style={{
                padding: '8px 16px',
                backgroundColor: '#4caf50',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Save Scenario
            </button>
            <button
              onClick={calculateAllocation}
              style={{
                padding: '8px 16px',
                backgroundColor: '#1976d2',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <span>↻</span>
              <span>Recalculate</span>
            </button>
          </div>
        </div>

        {/* Asset Allocation Sliders */}
        <div style={{ 
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '20px',
          marginBottom: '20px'
        }}>
          {localAssetClasses.map((assetClass: AssetClass) => {
            const assetKey = assetClass.name.toLowerCase() as keyof Allocation;
            return (
              <div 
                key={assetClass.name} 
                style={{ 
                  backgroundColor: 'white',
                  padding: '15px',
                  borderRadius: '6px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                }}
                onMouseEnter={() => setSelectedAsset(assetKey)}
                onMouseLeave={() => setSelectedAsset(null)}
              >
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  marginBottom: '10px'
                }}>
                  <span style={{ fontWeight: 'bold' }}>{assetClass.name}</span>
                  <span>{allocation[assetKey]}%</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={allocation[assetKey]}
                  onChange={(e) => handleAllocationChange(assetKey, Number(e.target.value))}
                  style={{ width: '100%' }}
                />
                <div style={{ 
                  fontSize: '0.9em',
                  color: '#666',
                  marginTop: '5px'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                    <span>Return: {assetClass.longTermReturn}%</span>
                    <span>Vol: {assetClass.volatility}%</span>
                  </div>
                  {selectedAsset === assetKey && (
                    <div style={{ 
                      marginTop: '10px', 
                      backgroundColor: '#f8f9fa',
                      padding: '8px',
                      borderRadius: '4px',
                      fontSize: '0.85em'
                    }}>
                      <div style={{ marginBottom: '5px' }}>Correlations:</div>
                      {localAssetClasses.map((other: AssetClass) => (
                        <div key={other.name} style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span>{other.name}:</span>
                          <span>
                            {((correlationData as CorrelationMatrix).correlations[assetKey as AssetType]?.[other.name.toLowerCase() as AssetType] ?? 0).toFixed(2)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Portfolio Metrics */}
        <div style={{ 
          padding: '20px', 
          backgroundColor: 'white', 
          borderRadius: '6px',
          marginTop: '20px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            marginBottom: '10px',
            color: totalAllocation === 100 ? 'green' : totalAllocation > 100 ? 'red' : 'orange'
          }}>
            <strong>Total Allocation:</strong>
            <span>{totalAllocation}% {totalAllocation === 100 ? '(Properly Allocated)' : 
                                    totalAllocation > 100 ? '(Over Allocated)' : 
                                    '(Under Allocated)'}</span>
          </div>
          <p style={{ color: '#666', marginBottom: '15px' }}>
            These metrics help evaluate your portfolio's risk-return characteristics. The expected return and volatility are weighted 
            based on your asset allocation, while the Sharpe ratio measures risk-adjusted performance.
          </p>
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: '10px' 
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <strong>Portfolio Expected Return</strong>
                <Tooltip text="The weighted average of expected returns from all assets in your portfolio" />
              </div>
              <span>{expectedReturn.toFixed(1)}%</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <strong>Portfolio Volatility</strong>
                <Tooltip text="A measure of risk that accounts for how assets move together (correlations)" />
              </div>
              <span>{portfolioVolatility.toFixed(1)}%</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <strong>Sharpe Ratio</strong>
                <Tooltip text="A measure of risk-adjusted returns. Higher is better. Above 1 is good, above 2 is excellent." />
              </div>
              <span>{sharpeRatio.toFixed(2)}</span>
            </div>
            <div style={{ 
              fontSize: '0.9em',
              color: '#666',
              marginTop: '5px',
              borderTop: '1px solid #eee',
              paddingTop: '10px'
            }}>
              <div>Risk-Free Rate: {riskFreeRate}%</div>
              <div>Sharpe Ratio = (Return - Risk Free Rate) / Volatility</div>
              <div style={{ marginTop: '5px' }}>
                <strong>Note:</strong> Portfolio volatility calculation includes asset correlations
              </div>
            </div>
          </div>
        </div>

        {/* Saved Scenarios */}
        {savedScenarios.length > 0 && (
          <div style={{ 
            backgroundColor: 'white', 
            padding: '20px', 
            borderRadius: '6px',
            marginTop: '20px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{ marginBottom: '15px' }}>Saved Scenarios</h3>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ padding: '8px', borderBottom: '2px solid #ddd', textAlign: 'left' }}>Scenario</th>
                    <th style={{ padding: '8px', borderBottom: '2px solid #ddd', textAlign: 'right' }}>Expected Return</th>
                    <th style={{ padding: '8px', borderBottom: '2px solid #ddd', textAlign: 'right' }}>Volatility</th>
                    <th style={{ padding: '8px', borderBottom: '2px solid #ddd', textAlign: 'right' }}>Sharpe Ratio</th>
                    <th style={{ padding: '8px', borderBottom: '2px solid #ddd', textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {savedScenarios.map((scenario, index) => (
                    <tr 
                      key={index}
                      onClick={() => handleRestoreScenario(index)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f5f5f5';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '';
                      }}
                      style={{ 
                        cursor: 'pointer',
                        transition: 'background-color 0.2s'
                      }}
                      title="Click to restore this allocation"
                    >
                      <td style={{ padding: '8px', borderBottom: '1px solid #ddd' }}>{scenario.name}</td>
                      <td style={{ padding: '8px', borderBottom: '1px solid #ddd', textAlign: 'right' }}>
                        {scenario.expectedReturn.toFixed(1)}%
                      </td>
                      <td style={{ padding: '8px', borderBottom: '1px solid #ddd', textAlign: 'right' }}>
                        {scenario.volatility.toFixed(1)}%
                      </td>
                      <td style={{ padding: '8px', borderBottom: '1px solid #ddd', textAlign: 'right' }}>
                        {scenario.sharpeRatio.toFixed(2)}
                      </td>
                      <td style={{ padding: '8px', borderBottom: '1px solid #ddd', textAlign: 'center' }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedReport(index);
                          }}
                          style={{
                            padding: '4px 8px',
                            backgroundColor: '#4caf50',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                          title="Generate Report"
                        >
                          📄 Report
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Portfolio Simulation */}
        <div style={{ 
          backgroundColor: 'white', 
          padding: '20px', 
          borderRadius: '6px',
          marginTop: '20px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{ marginBottom: '10px' }}>Portfolio Simulation</h3>
          <p style={{ color: '#666', marginBottom: '20px' }}>
            This simulation shows potential portfolio values over time based on historical market behavior. The shaded areas represent 
            different confidence ranges, with the darker middle band showing more likely outcomes and the outer bands showing potential 
            extremes.
          </p>
          <PortfolioSimulationChart
            assetClasses={localAssetClasses}
            allocation={allocation}
            correlationMatrix={correlationData as CorrelationMatrix}
            initialInvestment={initialInvestment}
            monthlyContribution={monthlyContribution}
            targetAmount={targetAmount}
            years={years}
            iterations={1000}
          />
        </div>

        {/* Risk/Return Heatmap */}
        <div style={{ 
          backgroundColor: 'white', 
          padding: '20px', 
          borderRadius: '6px',
          marginTop: '20px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <RiskReturnHeatmap
            assetClasses={localAssetClasses}
            currentAllocation={allocation}
          />
        </div>
      </div>

      {/* Report Modal */}
      {selectedReport !== null && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          zIndex: 1000,
          overflowY: 'auto',
          padding: '20px'
        }}>
          <ScenarioReport
            scenario={savedScenarios[selectedReport]}
            assetClasses={localAssetClasses}
            correlationMatrix={correlationData as CorrelationMatrix}
            initialInvestment={initialInvestment}
            monthlyContribution={monthlyContribution}
            targetAmount={targetAmount}
            years={years}
            riskTolerance={riskTolerance}
            username={username}
            onClose={() => setSelectedReport(null)}
          />
        </div>
      )}
    </div>
  );
};

export default StrategicAssetAllocation;
